import React from "react";
import { Box, Flex, Heading, HStack, Icon, SimpleGrid, Text } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import { motion } from "framer-motion";
import {
  PiGavelDuotone, PiUsersFourDuotone, PiGlobeDuotone, PiChartBarDuotone, PiChatsDuotone, PiSlidersHorizontalDuotone, PiCheckBold,
  PiUsersThreeDuotone, PiEyeDuotone, PiLockDuotone, PiShieldCheckDuotone, PiPercentDuotone, PiListChecksDuotone,
  PiCoinsDuotone, PiBuildingsDuotone, PiScrollDuotone, PiSealCheckDuotone
} from "react-icons/pi";
import { CreateVotingViewModel, VotingGoal } from "../CreateVotingView/CreateVotingViewModel";
import { GLASS, drgba } from "../../components/glassUi";

const MotionDiv = motion.div;

type Attr = { icon: React.ElementType; label: string; value: string };
type Goal = { goal: VotingGoal; icon: React.ElementType; title: string; desc: string; color: string; grad: string; attrs: Attr[] };

const GOALS: Goal[] = [
  {
    goal: "board_resolution",
    icon: PiGavelDuotone,
    title: "Uchwała organu",
    desc: "Zarząd, rada lub walne głosuje nad decyzją.",
    color: "#2563eb",
    grad: "linear-gradient(140deg,#2563eb,#4f8bff)",
    attrs: [
      { icon: PiUsersThreeDuotone, label: "Kto głosuje", value: "Lista imienna" },
      { icon: PiEyeDuotone, label: "Wynik", value: "Jawny" },
      { icon: PiPercentDuotone, label: "Kworum", value: "50%" }
    ]
  },
  {
    goal: "election",
    icon: PiUsersFourDuotone,
    title: "Wybory",
    desc: "Wybór osób z listy kandydatów.",
    color: "#6366f1",
    grad: "linear-gradient(140deg,#6366f1,#8b8ffb)",
    attrs: [
      { icon: PiUsersThreeDuotone, label: "Kto głosuje", value: "Lista imienna" },
      { icon: PiLockDuotone, label: "Głos", value: "Tajny" },
      { icon: PiListChecksDuotone, label: "Wybór", value: "1 z listy" }
    ]
  },
  {
    goal: "open_referendum",
    icon: PiGlobeDuotone,
    title: "Referendum otwarte",
    desc: "Głosuje każdy zweryfikowany obywatel.",
    color: "#10b981",
    grad: "linear-gradient(140deg,#10b981,#34d9a4)",
    attrs: [
      { icon: PiUsersThreeDuotone, label: "Kto głosuje", value: "Każdy" },
      { icon: PiLockDuotone, label: "Głos", value: "Tajny · publiczny" },
      { icon: PiShieldCheckDuotone, label: "Weryfikacja", value: "mObywatel" }
    ]
  },
  {
    goal: "poll",
    icon: PiChartBarDuotone,
    title: "Ankieta / sondaż",
    desc: "Szybkie zebranie opinii, nieformalnie.",
    color: "#f59e0b",
    grad: "linear-gradient(140deg,#f59e0b,#fbbf3d)",
    attrs: [
      { icon: PiUsersThreeDuotone, label: "Kto głosuje", value: "Zapisy / link" },
      { icon: PiEyeDuotone, label: "Wynik", value: "Jawny" },
      { icon: PiPercentDuotone, label: "Kworum", value: "Brak" }
    ]
  },
  {
    goal: "consultation",
    icon: PiChatsDuotone,
    title: "Konsultacje społeczne",
    desc: "Zbieranie stanowisk w wielu pytaniach.",
    color: "#0d9488",
    grad: "linear-gradient(140deg,#0d9488,#2dd4bf)",
    attrs: [
      { icon: PiUsersThreeDuotone, label: "Kto głosuje", value: "Otwarte / zapisy" },
      { icon: PiListChecksDuotone, label: "Format", value: "Wiele pytań" },
      { icon: PiEyeDuotone, label: "Wynik", value: "Publiczny" }
    ]
  },
  {
    goal: "budget",
    icon: PiCoinsDuotone,
    title: "Budżet obywatelski",
    desc: "Mieszkańcy wybierają projekty do sfinansowania.",
    color: "#0ea5e9",
    grad: "linear-gradient(140deg,#0ea5e9,#38bdf8)",
    attrs: [
      { icon: PiUsersThreeDuotone, label: "Kto głosuje", value: "Mieszkańcy (zapisy)" },
      { icon: PiLockDuotone, label: "Głos", value: "Tajny" },
      { icon: PiSealCheckDuotone, label: "Weryfikacja", value: "mObywatel" }
    ]
  },
  {
    goal: "hoa",
    icon: PiBuildingsDuotone,
    title: "Uchwała wspólnoty",
    desc: "Wspólnota lub spółdzielnia głosuje uchwałę.",
    color: "#64748b",
    grad: "linear-gradient(140deg,#64748b,#94a3b8)",
    attrs: [
      { icon: PiUsersThreeDuotone, label: "Kto głosuje", value: "Lista lokali" },
      { icon: PiEyeDuotone, label: "Wynik", value: "Jawny" },
      { icon: PiPercentDuotone, label: "Kworum", value: "50%" }
    ]
  },
  {
    goal: "petition",
    icon: PiScrollDuotone,
    title: "Petycja / poparcie",
    desc: "Zbierasz poparcie pod inicjatywą.",
    color: "#db2777",
    grad: "linear-gradient(140deg,#db2777,#f472b6)",
    attrs: [
      { icon: PiUsersThreeDuotone, label: "Kto głosuje", value: "Każdy" },
      { icon: PiEyeDuotone, label: "Podpis", value: "Jawny" },
      { icon: PiSealCheckDuotone, label: "Weryfikacja", value: "mObywatel" }
    ]
  },
  {
    goal: "custom",
    icon: PiSlidersHorizontalDuotone,
    title: "Od zera",
    desc: "Pełna kontrola nad każdym ustawieniem.",
    color: "#7b2ff7",
    grad: "linear-gradient(140deg,#7b2ff7,#a56bff)",
    attrs: [
      { icon: PiUsersThreeDuotone, label: "Kto głosuje", value: "Ty ustawiasz" },
      { icon: PiEyeDuotone, label: "Wynik", value: "Do wyboru" },
      { icon: PiSlidersHorizontalDuotone, label: "Reszta", value: "Elastyczna" }
    ]
  }
];

const GoalCard: React.FC<{ g: Goal; active: boolean; onSelect: () => void; index: number }> = ({ g, active, onSelect, index }) => (
  <MotionDiv
    onClick={onSelect}
    initial={{ opacity: 0, y: 22 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.07, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
    whileHover={{ y: -5 }}
    whileTap={{ scale: 0.985 }}
    style={{ cursor: "pointer", height: "100%" }}
  >
    <Box
      position="relative"
      h="100%"
      minH={{ base: "auto", lg: "268px" }}
      p="20px"
      borderRadius="22px"
      overflow="hidden"
      display="flex"
      flexDirection="column"
      bg={active ? drgba(g.color, 0.06) : "rgba(255,255,255,0.55)"}
      border="2px solid" borderColor={active ? g.color : "rgba(24,12,48,0.08)"}
      boxShadow={active ? `0 16px 38px -22px ${drgba(g.color, 0.6)}` : "none"}
      transition={`box-shadow 0.25s, border-color 0.2s, background 0.2s`}
    >
      {/* poświata akcentu */}
      <Box position="absolute" top="-64px" right="-52px" w="200px" h="200px" borderRadius="full" pointerEvents="none" sx={{ background: `radial-gradient(circle, ${drgba(g.color, active ? 0.28 : 0.12)}, transparent 70%)` }} transition="all 0.3s" />

      <Flex justify="space-between" align="flex-start" position="relative">
        <Flex w="56px" h="56px" borderRadius="16px" align="center" justify="center" style={{ background: active ? g.grad : drgba(g.color, 0.12) }} boxShadow={active ? `0 12px 24px -10px ${drgba(g.color, 0.7)}` : undefined} transition="all 0.2s">
          <Icon as={g.icon} boxSize="28px" color={active ? "white" : g.color} />
        </Flex>
        <Flex w="26px" h="26px" borderRadius="full" align="center" justify="center" border="2px solid" borderColor={active ? g.color : "rgba(24,12,48,0.15)"} bg={active ? g.color : "transparent"} transition="all 0.16s">
          {active && (
            <MotionDiv initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 520, damping: 18 }}>
              <Icon as={PiCheckBold} boxSize="12px" color="white" />
            </MotionDiv>
          )}
        </Flex>
      </Flex>

      <Box mt="16px" position="relative">
        <Heading fontSize="19px" fontWeight="900" color="#1b1030" letterSpacing="-0.01em" lineHeight="1.15">{g.title}</Heading>
        <Text fontSize="13.5px" color="#6b6480" mt="5px" lineHeight="1.45">{g.desc}</Text>
      </Box>

      <Box mt="auto" pt="16px" position="relative">
        <Box borderTop="1px solid rgba(24,12,48,0.07)" pt="13px">
          <VStackAttrs attrs={g.attrs} color={g.color} />
        </Box>
      </Box>
    </Box>
  </MotionDiv>
);

const VStackAttrs: React.FC<{ attrs: Attr[]; color: string }> = ({ attrs, color }) => (
  <Flex direction="column" gap="9px">
    {attrs.map(a => (
      <Flex key={a.label} align="center" gap="9px">
        <Flex w="24px" h="24px" borderRadius="8px" align="center" justify="center" bg={drgba(color, 0.1)} flexShrink={0}>
          <Icon as={a.icon} boxSize="13px" color={color} />
        </Flex>
        <Text fontSize="12px" color="#8a8397" fontWeight="600">{a.label}</Text>
        <Text fontSize="12.5px" color="#241536" fontWeight="800" ml="auto" textAlign="right" noOfLines={1}>{a.value}</Text>
      </Flex>
    ))}
  </Flex>
);

export const StepBasics: React.FC<{ model: CreateVotingViewModel; stepper?: React.ReactNode }> = observer(({ model, stepper }) => (
  <Box borderRadius="24px" p={{ base: "22px", md: "30px" }} position="relative" overflow="hidden" sx={GLASS}>
    <Box position="absolute" top="-90px" right="-70px" w="300px" h="300px" borderRadius="full" pointerEvents="none" sx={{ background: `radial-gradient(circle, ${drgba("#7b2ff7", 0.08)}, transparent 70%)` }} />

    {/* Header połączony z kartą */}
    <Box position="relative" mb={{ base: "22px", md: "28px" }}>
      <Flex justify="space-between" align="center" gap="12px" mb="14px">
        <HStack spacing="9px">
          <Text fontSize="12px" fontWeight="800" letterSpacing="0.05em" color="#8a8397" textTransform="uppercase">Nowe głosowanie</Text>
          <Box px="8px" py="1px" borderRadius="full" bg={drgba("#7b2ff7", 0.12)}><Text fontSize="10.5px" fontWeight="800" color="#6b21d6">v2</Text></Box>
        </HStack>
        {stepper}
      </Flex>
      <Heading fontSize={{ base: "24px", md: "30px" }} fontWeight="900" color="#0f0820" letterSpacing="-0.03em" lineHeight="1.08">Co chcesz stworzyć?</Heading>
      <Text fontSize={{ base: "14px", md: "15px" }} color="#6b6480" mt="6px" maxW="560px">Wybierz punkt startu — resztę ułożysz na jednym płótnie.</Text>
    </Box>

    <SimpleGrid position="relative" columns={{ base: 1, md: 3 }} spacing="14px" alignItems="stretch">
      {GOALS.map((g, i) => (
        <GoalCard key={g.goal} g={g} index={i} active={model.votingGoal === g.goal} onSelect={() => model.applyTemplate(g.goal)} />
      ))}
    </SimpleGrid>
  </Box>
));
